import { useQuery } from '@tanstack/react-query';
import { queryDataSource } from '../../api/query';
import type { WidgetAggregation } from '../../types/panelConfig';

export const accentMap: Record<string, string> = {
  blue: 'var(--color-primary)',
  green: 'var(--color-positive)',
  purple: 'var(--color-tier-ileri)',
  orange: 'var(--color-warning)',
  red: 'var(--color-negative)',
  cyan: '#0e7490',
};

export const defaultPalette = ['#0f172a', '#475569', '#b45309', '#6b21a8', '#16a34a', '#0e7490'];

export const currencyFmt = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  maximumFractionDigits: 0,
});

export const compactFmt = new Intl.NumberFormat('tr-TR', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

export function useWidgetData(
  sourceId: number | null | undefined,
  widgetKey: string,
  parameters?: Record<string, string>,
) {
  return useQuery({
    queryKey: ['dashboard-widget', widgetKey, sourceId, parameters ?? null],
    queryFn: async () => {
      if (!sourceId) return [] as Record<string, unknown>[];
      const res = await queryDataSource({ sourceId, parameters });
      // Distinguish transient failure from a legitimately empty result: a
      // failed query throws so react-query retries (retry: 2) and keeps the
      // cached data for THIS key on screen during the retry. success===true
      // with an empty array stays empty (genuine "no data" → "—").
      if (!res.success) throw new Error(res.errorMessage ?? 'Sorgu başarısız');
      return (res.data as Record<string, unknown>[]) ?? [];
    },
    enabled: !!sourceId,
    staleTime: 1000 * 60 * 5,
    // NOT keepPreviousData: değiştirilen periyot yeni bir queryKey üretir; önceki
    // periyodun verisini tutmak yerine BİLEREK ilk-yükleme gibi loading (skeleton)
    // gösteriyoruz. Aksi halde widget'lar eski değerden yenisine tek tek atlıyordu
    // ve kullanıcı verinin yenilendiğini fark etmiyordu (kafa karışıklığı).
    retry: 2,
  });
}

export function aggregate(
  rows: Record<string, unknown>[],
  field: string,
  method: WidgetAggregation,
): number {
  if (rows.length === 0) return 0;
  const values = rows.map((r) => Number(r[field]) || 0);
  switch (method) {
    case 'avg':
      return values.reduce((a, b) => a + b, 0) / values.length;
    case 'count':
      return values.length;
    case 'sum':
    default:
      return values.reduce((a, b) => a + b, 0);
  }
}

export function groupByCategory(
  rows: Record<string, unknown>[],
  categoryField: string,
  valueField: string,
  method: WidgetAggregation = 'sum',
): Array<{ name: string; value: number }> {
  if (rows.length === 0) return [];
  const buckets = new Map<string, { sum: number; count: number }>();
  for (const row of rows) {
    const key = String(row[categoryField] ?? '');
    if (!key) continue;
    const val = Number(row[valueField] ?? 0);
    const cur = buckets.get(key) ?? { sum: 0, count: 0 };
    cur.sum += val;
    cur.count += 1;
    buckets.set(key, cur);
  }
  return Array.from(buckets.entries()).map(([name, { sum, count }]) => {
    let value: number;
    switch (method) {
      case 'avg':
        value = count > 0 ? sum / count : 0;
        break;
      case 'count':
        value = count;
        break;
      case 'sum':
      default:
        value = sum;
    }
    return { name, value: Math.round(value) };
  });
}
